// SVSchreiberPage.jsx — SV-Wertungsportal (Dark Theme)
// Verteidiger = benannte Person aus Teilnehmerliste
// Angreifer   = anonymes Score-Panel (keine Personenauswahl)
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || '';

const SURFACE  = '#111118';
const SURFACE2 = '#1a1a26';
const BORDER   = 'rgba(255,255,255,0.09)';
const VERT_COL = '#7c3aed';
const VERT_BG  = 'rgba(124,58,237,0.12)';
const VERT_BD  = 'rgba(124,58,237,0.35)';
const ANG_COL  = '#ef4444';
const ANG_BG   = 'rgba(239,68,68,0.12)';
const ANG_BD   = 'rgba(239,68,68,0.35)';
const GREEN    = '#10b981';

const UEBUNGEN = 5;

const LEVEL_CFG = {
  farb:    { label: '🟡 Farbgurt',    presets: [6.0, 6.5, 7.0, 7.5, 8.0],   default: 7.0 },
  schwarz: { label: '⬛ Schwarzgurt', presets: [8.0, 8.5, 9.0, 9.5, 10.0], default: 9.0 },
};

function getSVSubTyp(divCode = '') {
  const c = (divCode || '').toUpperCase();
  if (c.startsWith('RSD')) return 'Real Self Defense';
  if (c.startsWith('TSD')) return 'Team Self Defense';
  if (c.startsWith('SD'))  return '1 vs. 1';
  return 'Selbstverteidigung';
}

function formatTime(s) {
  const sec = Math.max(0, Math.floor(s));
  return `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`;
}

/* ── Angreifer Score-Panel (anonym, keine Personenauswahl) ── */
function AngreiferPanel({ level, activeUebung, score, onScoreChange, saved, disabled }) {
  const lc = LEVEL_CFG[level];

  return (
    <div style={{
      background: SURFACE, border: `1px solid ${ANG_BD}`, borderRadius: 14,
      padding: '1.1rem 1.25rem', display: 'flex', flexDirection: 'column', gap: '0.85rem',
      flex: 1, minWidth: 0,
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <span style={{ fontSize: '1.1rem' }}>⚔️</span>
        <span style={{ fontWeight: 700, color: ANG_COL, fontSize: '0.9rem', letterSpacing: '0.04em' }}>ANGREIFER</span>
        {saved && (
          <span style={{ marginLeft: 'auto', fontSize: '0.72rem', color: GREEN, background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: 5, padding: '0.1rem 0.45rem' }}>
            ✓ Gespeichert
          </span>
        )}
      </div>

      {/* Score-Anzeige */}
      <div style={{ textAlign: 'center', fontSize: '3.5rem', fontWeight: 900, color: ANG_COL, lineHeight: 1, padding: '0.5rem 0' }}>
        {score.toFixed(1)}
      </div>

      {/* Preset-Buttons */}
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${lc.presets.length}, 1fr)`, gap: '0.45rem' }}>
        {lc.presets.map(p => {
          const isAct = Math.abs(p - score) < 0.01;
          return (
            <button key={p} disabled={disabled}
              onClick={() => onScoreChange(p)}
              style={{
                padding: '0.6rem 0', background: isAct ? ANG_COL : 'rgba(255,255,255,0.06)',
                border: `1px solid ${isAct ? ANG_COL : BORDER}`, borderRadius: 7,
                color: isAct ? '#fff' : 'rgba(255,255,255,0.55)', fontWeight: 700,
                fontSize: '0.95rem', cursor: disabled ? 'not-allowed' : 'pointer', transition: 'all 0.12s',
              }}>
              {p.toFixed(1)}
            </button>
          );
        })}
      </div>

      {/* ±0.1 Feineinstellung */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem' }}>
        <button disabled={disabled}
          onClick={() => onScoreChange(Math.max(0, parseFloat((score - 0.1).toFixed(1))))}
          style={{ background: 'rgba(255,255,255,0.07)', border: `1px solid ${BORDER}`, borderRadius: 8, width: 44, height: 44, color: '#fff', fontSize: '1.2rem', fontWeight: 700, cursor: disabled ? 'not-allowed' : 'pointer' }}>
          −
        </button>
        <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.25)' }}>±0.1</span>
        <button disabled={disabled}
          onClick={() => onScoreChange(Math.min(10, parseFloat((score + 0.1).toFixed(1))))}
          style={{ background: 'rgba(255,255,255,0.07)', border: `1px solid ${BORDER}`, borderRadius: 8, width: 44, height: 44, color: '#fff', fontSize: '1.2rem', fontWeight: 700, cursor: disabled ? 'not-allowed' : 'pointer' }}>
          +
        </button>
      </div>

      <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.2)', textAlign: 'center' }}>
        Angreifer-Qualität · Übung {activeUebung}
      </div>
    </div>
  );
}

/* ── Verteidiger Score-Panel (benannte Person) ── */
function VerteidigerPanel({ level, activeUebung, teilnehmer, selectedId, onSelect,
  scores, savedUebungen, onScoreChange, disabled }) {

  const lc = LEVEL_CFG[level];
  const tn = teilnehmer.find(t => t.wettkaempfer_id === selectedId) || null;
  const scoreKey = tn ? `${tn.wettkaempfer_id}_${activeUebung}` : null;
  const currentScore = scoreKey ? (scores[scoreKey] ?? lc.default) : lc.default;
  const isSaved = scoreKey ? !!savedUebungen[scoreKey] : false;

  const gesamt = tn
    ? Array.from({ length: UEBUNGEN }, (_, i) => i + 1).reduce((s, u) => {
        const k = `${tn.wettkaempfer_id}_${u}`;
        return s + (scores[k] !== undefined ? scores[k] : 0);
      }, 0)
    : 0;

  return (
    <div style={{
      background: SURFACE, border: `1px solid ${VERT_BD}`, borderRadius: 14,
      padding: '1.1rem 1.25rem', display: 'flex', flexDirection: 'column', gap: '0.85rem',
      flex: 1, minWidth: 0,
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <span style={{ fontSize: '1.1rem' }}>🛡️</span>
        <span style={{ fontWeight: 700, color: VERT_COL, fontSize: '0.9rem', letterSpacing: '0.04em' }}>VERTEIDIGER</span>
        {isSaved && (
          <span style={{ marginLeft: 'auto', fontSize: '0.72rem', color: GREEN, background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: 5, padding: '0.1rem 0.45rem' }}>
            ✓ Gespeichert
          </span>
        )}
      </div>

      {/* Teilnehmer-Auswahl */}
      <select
        value={selectedId || ''}
        onChange={e => onSelect(e.target.value ? Number(e.target.value) : null)}
        style={{
          background: SURFACE2, border: `1px solid ${VERT_BD}`, borderRadius: 8,
          padding: '0.45rem 0.75rem', color: '#e8e8f0', fontSize: '0.85rem',
          fontWeight: 600, width: '100%', cursor: 'pointer',
        }}
      >
        <option value="">— Verteidiger auswählen —</option>
        {teilnehmer.map(t => (
          <option key={t.wettkaempfer_id} value={t.wettkaempfer_id}>
            {t.vorname} {t.nachname} · {t.verein_name}
          </option>
        ))}
      </select>

      {tn ? (
        <>
          {/* Athlet-Info */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 0.75rem', background: VERT_BG, borderRadius: 8, border: `1px solid ${VERT_BD}` }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#fff' }}>
                {tn.vorname} {tn.nachname}
              </div>
              <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)', marginTop: 1 }}>
                {tn.verein_name} · {tn.gurtfarbe || '—'}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Gesamt</div>
              <div style={{ fontSize: '1.6rem', fontWeight: 900, color: VERT_COL, lineHeight: 1 }}>
                {gesamt.toFixed(1)}
              </div>
            </div>
          </div>

          {/* Score-Anzeige */}
          <div style={{ textAlign: 'center', fontSize: '3.5rem', fontWeight: 900, color: VERT_COL, lineHeight: 1, padding: '0.25rem 0' }}>
            {currentScore.toFixed(1)}
          </div>

          {/* Preset-Buttons */}
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${lc.presets.length}, 1fr)`, gap: '0.45rem' }}>
            {lc.presets.map(p => {
              const isAct = Math.abs(p - currentScore) < 0.01;
              return (
                <button key={p} disabled={disabled}
                  onClick={() => onScoreChange(scoreKey, p)}
                  style={{
                    padding: '0.6rem 0', background: isAct ? VERT_COL : 'rgba(255,255,255,0.06)',
                    border: `1px solid ${isAct ? VERT_COL : BORDER}`, borderRadius: 7,
                    color: isAct ? '#fff' : 'rgba(255,255,255,0.55)', fontWeight: 700,
                    fontSize: '0.95rem', cursor: disabled ? 'not-allowed' : 'pointer', transition: 'all 0.12s',
                  }}>
                  {p.toFixed(1)}
                </button>
              );
            })}
          </div>

          {/* ±0.1 Feineinstellung */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem' }}>
            <button disabled={disabled}
              onClick={() => onScoreChange(scoreKey, Math.max(0, parseFloat(((scores[scoreKey] ?? lc.default) - 0.1).toFixed(1))))}
              style={{ background: 'rgba(255,255,255,0.07)', border: `1px solid ${BORDER}`, borderRadius: 8, width: 44, height: 44, color: '#fff', fontSize: '1.2rem', fontWeight: 700, cursor: disabled ? 'not-allowed' : 'pointer' }}>
              −
            </button>
            <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.25)' }}>±0.1</span>
            <button disabled={disabled}
              onClick={() => onScoreChange(scoreKey, Math.min(10, parseFloat(((scores[scoreKey] ?? lc.default) + 0.1).toFixed(1))))}
              style={{ background: 'rgba(255,255,255,0.07)', border: `1px solid ${BORDER}`, borderRadius: 8, width: 44, height: 44, color: '#fff', fontSize: '1.2rem', fontWeight: 700, cursor: disabled ? 'not-allowed' : 'pointer' }}>
              +
            </button>
          </div>

          {/* Übungen-Fortschritt */}
          <div style={{ display: 'flex', gap: 3 }}>
            {Array.from({ length: UEBUNGEN }, (_, i) => i + 1).map(u => (
              <div key={u} style={{
                flex: 1, height: 5, borderRadius: 3,
                background: savedUebungen[`${tn.wettkaempfer_id}_${u}`] ? VERT_COL : 'rgba(255,255,255,0.1)',
                transition: 'background 0.2s',
              }} />
            ))}
          </div>
        </>
      ) : (
        <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.2)', fontSize: '0.85rem', padding: '2rem 0' }}>
          Verteidiger auswählen
        </div>
      )}
    </div>
  );
}

/* ── Hauptkomponente ── */
export default function SVSchreiberPage() {
  const [searchParams] = useSearchParams();
  const turnierId = searchParams.get('turnierId');
  const initKatId = searchParams.get('kategorieId');
  const token     = localStorage.getItem('token');

  const [kategorien,    setKategorien]    = useState([]);
  const [selectedKat,   setSelectedKat]   = useState(null);
  const [teilnehmer,    setTeilnehmer]    = useState([]);
  const [loading,       setLoading]       = useState(true);

  const [vertId,        setVertId]        = useState(null);  // Verteidiger wettkaempfer_id
  const [activeUebung,  setActiveUebung]  = useState(1);
  const [level,         setLevel]         = useState('farb');

  // Verteidiger scores: key = `${wkId}_${uebung}`, value = number
  const [vertScores,    setVertScores]    = useState({});
  const [savedUebungen, setSavedUebungen] = useState({});

  // Angreifer score: ein Wert pro aktiver Übung (anonym, nicht personen-gebunden)
  const lc0 = LEVEL_CFG['farb'];
  const [angScores,     setAngScores]     = useState(
    Object.fromEntries(Array.from({ length: UEBUNGEN }, (_, i) => [`ang_${i + 1}`, lc0.default]))
  );
  const [angSaved,      setAngSaved]      = useState({});  // { `ang_${u}`: true }

  const [saving,        setSaving]        = useState(false);
  const [timerSec,      setTimerSec]      = useState(180);
  const [timerRunning,  setTimerRunning]  = useState(false);
  const timerRef = useRef(null);

  /* ── Timer ── */
  useEffect(() => {
    if (timerRunning && timerSec > 0) {
      timerRef.current = setInterval(() => setTimerSec(p => p - 1), 1000);
    } else if (timerSec === 0) {
      setTimerRunning(false);
    }
    return () => clearInterval(timerRef.current);
  }, [timerRunning, timerSec]);

  /* ── Kategorien laden ── */
  const loadKategorien = useCallback(async () => {
    if (!turnierId) return;
    try {
      const r = await fetch(`${API_URL}/api/formen/sv/${turnierId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      if (d.success) setKategorien(d.data);
    } catch (e) { console.error(e); }
  }, [turnierId, token]);

  /* ── Kategorie-Details laden ── */
  const loadKategorie = useCallback(async (katId) => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/formen/kategorie/${katId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      if (d.success) {
        const kat  = d.data.kategorie;
        const list = d.data.teilnehmer || [];
        setSelectedKat(kat);
        setTeilnehmer(list);
        setVertId(null);
        setActiveUebung(1);

        // Vorhandene Verteidiger-Wertungen laden
        const existingScores = {};
        const existingSaved  = {};
        list.forEach(tn => {
          (tn.wertungen || []).forEach(w => {
            const key = `${tn.wettkaempfer_id}_${w.kampfrichter_nummer}`;
            existingScores[key] = parseFloat(w.punkte);
            existingSaved[key]  = true;
          });
        });
        setVertScores(existingScores);
        setSavedUebungen(existingSaved);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [token]);

  /* ── Init ── */
  useEffect(() => {
    const init = async () => {
      await loadKategorien();
      if (initKatId) await loadKategorie(initKatId);
      else setLoading(false);
    };
    init();
  }, [loadKategorien, loadKategorie, initKatId]);

  /* ── Level-Wechsel → Angreifer-Default neu setzen ── */
  useEffect(() => {
    const def = LEVEL_CFG[level].default;
    setAngScores(prev => {
      const next = { ...prev };
      for (let u = 1; u <= UEBUNGEN; u++) {
        if (next[`ang_${u}`] === undefined) next[`ang_${u}`] = def;
      }
      return next;
    });
  }, [level]);

  /* ── Wertung speichern ── */
  const saveWertung = async () => {
    if (saving || !selectedKat || !vertId) return;
    setSaving(true);

    const lc = LEVEL_CFG[level];

    // Verteidiger speichern
    const vertKey  = `${vertId}_${activeUebung}`;
    const vertPkte = vertScores[vertKey] ?? lc.default;

    try {
      const r = await fetch(`${API_URL}/api/formen/wertung`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          kategorie_id:    selectedKat.kategorie_id,
          wettkaempfer_id: vertId,
          wertungen: [{ kampfrichter_nummer: activeUebung, punkte: vertPkte, notiz: '' }],
          partial: true,
        }),
      });
      const d = await r.json();
      if (d.success) {
        setSavedUebungen(prev => ({ ...prev, [vertKey]: true }));
        setAngSaved(prev => ({ ...prev, [`ang_${activeUebung}`]: true }));

        // Nächste Übung
        if (activeUebung < UEBUNGEN) setActiveUebung(u => u + 1);
        await loadKategorie(selectedKat.kategorie_id);
      }
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  /* ── Gesamt-Punkte eines Verteidigers ── */
  const getGesamtVert = (tn) =>
    Array.from({ length: UEBUNGEN }, (_, i) => i + 1)
      .reduce((s, u) => s + (vertScores[`${tn.wettkaempfer_id}_${u}`] ?? 0), 0);

  const svTyp = getSVSubTyp(selectedKat?.division_code);
  const angScore = angScores[`ang_${activeUebung}`] ?? LEVEL_CFG[level].default;
  const angIsSaved = !!angSaved[`ang_${activeUebung}`];

  if (!turnierId) {
    return (
      <div style={{ background: '#0a0a0f', color: '#fff', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'system-ui, sans-serif' }}>
        <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.4)' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🛡️</div>
          <p>Kein Turnier ausgewählt.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: '#0a0a0f', color: '#e8e8f0', minHeight: '100vh', fontFamily: 'system-ui,-apple-system,sans-serif', display: 'flex', flexDirection: 'column' }}>

      {/* ── Top-Bar ── */}
      <div style={{ background: SURFACE, borderBottom: `1px solid ${BORDER}`, padding: '0.6rem 1.25rem', display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
        <select
          value={selectedKat?.kategorie_id || ''}
          onChange={e => { if (e.target.value) loadKategorie(e.target.value); }}
          style={{ background: SURFACE2, border: `1px solid ${VERT_BD}`, borderRadius: 7, padding: '0.4rem 0.75rem', color: '#e8e8f0', fontSize: '0.88rem', fontWeight: 600, minWidth: 200, cursor: 'pointer' }}
        >
          <option value="">— Kategorie wählen —</option>
          {kategorien.map(k => (
            <option key={k.kategorie_id} value={k.kategorie_id}>{k.kategorie_name} ({k.division_code})</option>
          ))}
        </select>

        {selectedKat && (
          <span style={{ fontSize: '0.8rem', background: VERT_BG, border: `1px solid ${VERT_BD}`, borderRadius: 6, padding: '0.25rem 0.65rem', fontWeight: 600, color: VERT_COL }}>
            {svTyp}
          </span>
        )}

        {/* Level-Wahl */}
        {Object.entries(LEVEL_CFG).map(([key, cfg]) => (
          <button key={key} onClick={() => setLevel(key)}
            style={{ padding: '0.3rem 0.75rem', background: level === key ? VERT_BG : 'rgba(255,255,255,0.04)', border: `1px solid ${level === key ? VERT_BD : BORDER}`, borderRadius: 6, color: level === key ? VERT_COL : 'rgba(255,255,255,0.4)', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer' }}>
            {cfg.label}
          </button>
        ))}

        <span style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.35)', marginLeft: 'auto' }}>
          {teilnehmer.length} Teilnehmer
        </span>

        {/* Timer */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: SURFACE2, border: `1px solid ${BORDER}`, borderRadius: 8, padding: '0.3rem 0.75rem' }}>
          <span style={{ fontSize: '1.1rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: timerSec < 30 ? '#ef4444' : '#e8e8f0' }}>
            {formatTime(timerSec)}
          </span>
          <button onClick={() => setTimerRunning(p => !p)}
            style={{ background: timerRunning ? 'rgba(16,185,129,0.15)' : 'rgba(255,255,255,0.07)', border: `1px solid ${timerRunning ? GREEN : BORDER}`, borderRadius: 5, width: 28, height: 28, color: timerRunning ? GREEN : '#e8e8f0', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 700 }}>
            {timerRunning ? '⏸' : '▶'}
          </button>
          <button onClick={() => { setTimerSec(180); setTimerRunning(false); }}
            style={{ background: 'rgba(255,255,255,0.05)', border: `1px solid ${BORDER}`, borderRadius: 5, width: 28, height: 28, color: 'rgba(255,255,255,0.4)', cursor: 'pointer', fontSize: '0.85rem' }}>
            ↺
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.3)', fontSize: '0.9rem' }}>
          Lade Kategorie…
        </div>
      ) : !selectedKat ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '0.75rem', color: 'rgba(255,255,255,0.3)' }}>
          <div style={{ fontSize: '3rem' }}>🛡️</div>
          <p style={{ margin: 0 }}>Bitte eine Kategorie auswählen.</p>
        </div>
      ) : (
        <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 260px', gap: 0, minHeight: 0, overflow: 'hidden' }}>

          {/* ── Scoring-Bereich ── */}
          <div style={{ padding: '1rem 1.25rem', display: 'flex', flexDirection: 'column', gap: '1rem', overflowY: 'auto' }}>

            {/* Übungen-Tabs */}
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              {Array.from({ length: UEBUNGEN }, (_, i) => i + 1).map(u => {
                const vertDone = vertId ? !!savedUebungen[`${vertId}_${u}`] : false;
                const active = u === activeUebung;
                return (
                  <button key={u} onClick={() => setActiveUebung(u)}
                    style={{
                      flex: 1, padding: '0.5rem 0',
                      background: active ? VERT_BG : vertDone ? 'rgba(16,185,129,0.1)' : 'rgba(255,255,255,0.04)',
                      border: `1px solid ${active ? VERT_BD : vertDone ? 'rgba(16,185,129,0.4)' : BORDER}`,
                      borderRadius: 8,
                      color: active ? VERT_COL : vertDone ? GREEN : 'rgba(255,255,255,0.35)',
                      fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer', transition: 'all 0.15s',
                    }}>
                    Üb. {u}{vertDone && !active ? ' ✓' : ''}
                  </button>
                );
              })}
            </div>

            {/* Zwei Panels nebeneinander */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <AngreiferPanel
                level={level}
                activeUebung={activeUebung}
                score={angScore}
                onScoreChange={val => setAngScores(prev => ({ ...prev, [`ang_${activeUebung}`]: val }))}
                saved={angIsSaved}
                disabled={saving}
              />
              <VerteidigerPanel
                level={level}
                activeUebung={activeUebung}
                teilnehmer={teilnehmer}
                selectedId={vertId}
                onSelect={setVertId}
                scores={vertScores}
                savedUebungen={savedUebungen}
                onScoreChange={(key, val) => setVertScores(prev => ({ ...prev, [key]: val }))}
                disabled={saving}
              />
            </div>

            {/* Submit */}
            <button
              onClick={saveWertung}
              disabled={saving || !vertId}
              style={{
                padding: '0.9rem',
                background: saving ? 'rgba(124,58,237,0.3)' : !vertId ? 'rgba(255,255,255,0.05)' : VERT_COL,
                border: 'none', borderRadius: 10, color: '#fff', fontWeight: 700, fontSize: '1rem',
                cursor: saving || !vertId ? 'not-allowed' : 'pointer', transition: 'background 0.15s', letterSpacing: '0.02em',
              }}>
              {saving ? '⏳ Speichere…' : !vertId ? 'Verteidiger auswählen' : `✓ Übung ${activeUebung} werten`}
            </button>

          </div>

          {/* ── Rangliste Sidebar (nur Verteidiger) ── */}
          <div style={{ background: SURFACE, borderLeft: `1px solid ${BORDER}`, padding: '1rem', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
            <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700, marginBottom: '0.25rem' }}>
              Rangliste · Verteidiger
            </div>

            {[...teilnehmer]
              .map(tn => ({ ...tn, gesamt: getGesamtVert(tn) }))
              .sort((a, b) => b.gesamt - a.gesamt)
              .map((tn, rank) => {
                const medals = ['🥇', '🥈', '🥉'];
                const isCurrent = tn.wettkaempfer_id === vertId;
                const done = Array.from({ length: UEBUNGEN }, (_, i) => i + 1).every(u => savedUebungen[`${tn.wettkaempfer_id}_${u}`]);

                return (
                  <div key={tn.wettkaempfer_id}
                    onClick={() => { setVertId(tn.wettkaempfer_id); setActiveUebung(1); }}
                    style={{
                      background: isCurrent ? VERT_BG : 'rgba(255,255,255,0.03)',
                      border: `1px solid ${isCurrent ? VERT_BD : BORDER}`,
                      borderRadius: 8, padding: '0.55rem 0.7rem', cursor: 'pointer', transition: 'all 0.12s',
                    }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <span style={{ fontSize: '0.9rem', flexShrink: 0 }}>{medals[rank] || `${rank + 1}.`}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: '0.8rem', color: isCurrent ? VERT_COL : '#e8e8f0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {tn.vorname} {tn.nachname}
                        </div>
                        <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {tn.verein_name}
                        </div>
                      </div>
                      <div style={{ fontWeight: 900, fontSize: '0.95rem', color: done ? GREEN : VERT_COL, flexShrink: 0 }}>
                        {tn.gesamt.toFixed(1)}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 3, marginTop: '0.35rem' }}>
                      {Array.from({ length: UEBUNGEN }, (_, i) => i + 1).map(u => (
                        <div key={u} style={{
                          flex: 1, height: 4, borderRadius: 2,
                          background: savedUebungen[`${tn.wettkaempfer_id}_${u}`] ? VERT_COL : 'rgba(255,255,255,0.1)',
                        }} />
                      ))}
                    </div>
                  </div>
                );
              })}

            {teilnehmer.length === 0 && (
              <p style={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.8rem', textAlign: 'center', margin: 0 }}>
                Keine Teilnehmer
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
